/**
 * src/pages/reports/reportPdf.js — the CEO AI report as a one-click PDF.
 *
 * Card P224 item 1. pdf-lib is already a dependency (package.json), so nothing
 * new is installed.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⛔⛔ THE CHOICE THE CARD ASKED ME TO MAKE: RASTERISE THE ON-SCREEN SVGs,
 * NOT RE-DRAW THE CHARTS FROM THE PAYLOAD.
 *
 * Drawing from the payload would be a SECOND IMPLEMENTATION OF SEVEN CHARTS.
 * pdf-lib has no chart API, so a funnel, a waterfall, a trend, two bar charts,
 * an ageing chart and a donut would each be hand-rolled from rectangles and
 * lines — and this repository has paid for that shape repeatedly: two stage
 * vocabularies, three markdown renderers, two receivables ageings. The second
 * copy always starts identical and always drifts, and here it would drift
 * INSIDE A DOCUMENT NOBODY RE-READS ON SCREEN, so a board pack could carry a
 * chart that no longer matched the dashboard with nothing to notice it.
 *
 * Rasterising gets "the same layout as on screen" BY CONSTRUCTION: the pixels
 * come from the chart the reader is looking at.
 *
 * ⚠ THE COSTS, STATED RATHER THAN DISCOVERED:
 *   · it needs a real browser — canvas and Image decoding — so jsdom cannot
 *     exercise it at all. Everything testable is therefore kept OUT of the
 *     rasteriser: the document MODEL is a pure function, the file name is a
 *     pure function, and collectCharts takes the capture function as an
 *     argument so a test can pass a stub.
 *   · ⛔ THE CAPTURE ITSELF IS _chartCapture.js's, NOT THIS FILE'S. See the
 *     note above collectCharts: I wrote a duplicate here first.
 *   · it captures the chart at its on-screen size. A chart that is not
 *     rendered (a section whose chart was dropped, or a collapsed panel) has
 *     no SVG, and the PDF omits it rather than inventing one.
 *   · fonts inside the SVG must be inlined or the canvas draws them with a
 *     default face. recharts writes its labels as <text> with a font-family
 *     from our tokens, which are system fonts — so they rasterise as the
 *     reader sees them, and an exotic webfont would be the thing to watch.
 * ═══════════════════════════════════════════════════════════════════════════
 */

import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { captureSvgPng } from './_chartCapture';
import { topNWithRemainder, foldCaption, TOP_N } from './_chartSizing';
import { kpiTiles, formatMetric, formatDelta, compactMoney } from './CeoReportView';
import { formatRangeLabel, formatGeneratedAt, comparisonCaption, stripStrayMarkdown } from './_reportFormat';

/** US Letter, in points. */
export const PAGE = Object.freeze({ width: 612, height: 792, margin: 54 });

/*
 * ⛔⛔ THE STANDARD FONTS ARE WinAnsi, AND THE SCREEN'S GLYPHS ARE NOT.
 *
 * MEASURED 1 Nov, on the first real build: pdf-lib threw
 * `WinAnsi cannot encode "▲" (0x25b2)` — and not on an edge case, but on the
 * KPI strip, because formatDelta() returns "▲ 12.1%". The same applies to the
 * ▼ beside it, the ☐ on every action, the · separating the header, the — in a
 * date range and the → between two dates. In other words, every single report
 * would have failed to download, and nothing short of actually building a
 * document would have shown it: the model is correct, the layout is correct,
 * and the encoder refuses at the last step.
 *
 * ⚠ TRANSLITERATED, NOT STRIPPED. "12.1%" with the arrow removed loses the
 * direction, which is the whole meaning of a delta; "+12.1%" keeps it. Only
 * after the known glyphs are mapped is anything still outside the encodable
 * range dropped, so an unexpected character costs a letter rather than the
 * document.
 *
 * ⛔ Embedding a Unicode font instead was rejected: it means shipping a font
 * file (hundreds of KB) in the bundle for six glyphs, and pdf-lib needs
 * fontkit to subset it. Six substitutions are cheaper and have no runtime.
 */
const GLYPHS = Object.freeze([
  [/\u25b2/g, '+'],        // ▲ up
  [/\u25bc/g, '-'],        // ▼ down
  [/\u2610/g, '[ ]'],      // ☐ checklist
  [/\u2192/g, '->'],       // → range arrow
  [/[\u2013\u2014]/g, '-'], // – — dashes
  [/\u00b7/g, '|'],        // · separator
  [/[\u2018\u2019]/g, "'"],
  [/[\u201c\u201d]/g, '"'],
  [/\u2026/g, '...'],
  [/[\u26a0\u2b50\u26d4]/g, '!'],   // ⚠ ⭐ ⛔, if prose ever carries them
]);

/** Transliterate to something WinAnsi can encode, then drop the rest. */
export function pdfSafe(text) {
  let out = String(text == null ? '' : text);
  for (const [re, to] of GLYPHS) out = out.replace(re, to);
  /* Anything still outside Latin-1 cannot be encoded by a standard font. */
  return out.replace(/[^\u0000-\u00ff]/g, '');
}

const plain = (t) => pdfSafe(stripStrayMarkdown(
  String(t == null ? '' : t).replace(/\*\*(.+?)\*\*/g, '$1')));

/**
 * The file name the card specifies.
 * ⚠ A pure function so it is testable, and so the name cannot drift between
 * the download and the audit row — both read this.
 */
export function pdfFileName(pipelineLabel, generatedAt) {
  const d = generatedAt ? new Date(generatedAt) : new Date();
  const ymd = Number.isNaN(d.getTime())
    ? new Date().toISOString().slice(0, 10)
    /* ⚠ LOCAL date, to match the "generated" stamp in the header. An ISO
       slice would name a file 2026-11-02 for a report a reader in New York
       generated on the evening of the 1st. */
    : [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'),
       String(d.getDate()).padStart(2, '0')].join('-');
  /* ⛔ Strip what a filesystem cannot take. A pipeline called "Stem Cell /
     Longevity" would otherwise create a directory on some platforms. */
  const safe = String(pipelineLabel || 'All pipelines')
    .replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, ' ').trim().slice(0, 60);
  return `ZignaGenix AI pipeline report - ${safe} - ${ymd}.pdf`;
}

/**
 * The document MODEL: an ordered list of blocks, derived from the same report
 * object and payload the screen renders.
 *
 * ⛔ PURE, AND THAT IS THE POINT. Every question worth testing — does the
 * header carry the real dates, are the KPI tiles the ones the screen shows,
 * is a section with no chart still present — is answered here, with no
 * canvas, no DOM and no pdf-lib.
 */
export function buildReportModel({ report, payload, pipelineLabel }) {
  if (!report) return null;
  const range = (payload && payload.period) || null;
  const blocks = [];

  blocks.push({ kind: 'header',
    title: 'Pipeline brief',
    pipeline: pdfSafe(pipelineLabel || 'All pipelines'),
    range: pdfSafe(formatRangeLabel(range)),
    generated: pdfSafe(formatGeneratedAt(payload && payload.generated_at)) });

  if (report.headline) blocks.push({ kind: 'headline', text: plain(report.headline) });
  if (report.summary) blocks.push({ kind: 'summary', text: plain(report.summary) });

  const tiles = kpiTiles(payload).map((t) => ({
    label: pdfSafe(t.label),
    value: pdfSafe(formatMetric(t.metric)),
    /* ⛔ "▲ 12.1%" becomes "+ 12.1%" — the DIRECTION is the meaning. */
    delta: pdfSafe(formatDelta(t.metric)),
    /* ⚠ The mapped caption, never the reason code — same rule as the screen
       (P214), because a PDF is the copy that outlives the session. */
    caption: pdfSafe(comparisonCaption(t.metric) || ''),
  }));
  if (tiles.length) {
    blocks.push({ kind: 'kpis', tiles });
    /*
     * ⛔ Card P227 D — the same caveat the screen shows. A PDF is the copy
     * that gets forwarded, so an unlabelled all-pipelines strike under a
     * single-pipeline heading is MORE dangerous on paper, not less.
     */
    if (payload && payload.headline_is_scoped === false
        && pipelineLabel && pipelineLabel !== 'All pipelines') {
      blocks.push({ kind: 'note', text: pdfSafe(
        `These tiles cover ALL pipelines. The brief below covers ${pipelineLabel}; `
        + 'its own figures are in the sections.') });
    }
  }

  report.sections.forEach((s, i) => {
    blocks.push({
      kind: 'section',
      index: i + 1,
      title: plain(s.title),
      status: s.status || null,
      bullets: (s.bullets || []).map(plain),
      /* The chart is referenced by TYPE; the bytes are fetched separately
         from the rendered DOM. A model with no chart is still a valid model. */
      chartType: s.chart ? s.chart.type : null,
      /*
       * ⛔⛔ Card P231 — THE FOLD CAPTION GOES IN AS TEXT, NOT INSIDE THE PNG.
       * The chart is rasterised; a caption burnt into the bitmap would be
       * unselectable, unsearchable and resampled to mush at print size. It is
       * computed here from the SAME helper the screen uses, so the PDF cannot
       * claim a different remainder from the panel it was captured from.
       */
      chartNote: s.chart ? foldNoteFor(s.chart, payload) : '',
    });
  });

  if (report.risks.length) {
    blocks.push({ kind: 'risks',
      items: report.risks.map((r) => ({ text: plain(r.text), severity: r.severity || null })) });
  }
  if (report.actions.length) {
    blocks.push({ kind: 'actions',
      items: report.actions.map((a) => ({
        text: plain(a.text),
        meta: pdfSafe([a.owner, a.due].filter(Boolean).join(', ')),
      })) });
  }
  return blocks;
}

/**
 * The "+n more stages · $X · n opps" line for a chart, or ''.
 *
 * ⚠ ONE SPEC, so the PDF and the screen fold identically. The noun and the
 * value formatter belong to the chart type; everything else is the shared
 * helper. A chart type absent from this table folds nothing, which is correct
 * for the funnel (a ladder, not a ranking — topping-and-tailing a progression
 * would change what it means) and for the waterfall and trend.
 */
const FOLD_SPEC = Object.freeze({
  bar_by_stage: { noun: 'stages', value: (r) => Number(r.open_value) || 0,
                  opps: (r) => Number(r.open_opps) || 0, fmt: compactMoney },
  bar_by_rep:   { noun: 'reps', value: (r) => Number(r.open_value) || 0,
                  opps: (r) => Number(r.open_opps) || 0, fmt: compactMoney },
  donut:        { noun: 'sources', value: (r) => Number(r.count) || 0,
                  opps: () => 0, fmt: (v) => `${v} leads` },
});

function resolve(root, path) {
  if (!root || typeof path !== 'string') return undefined;
  let node = root;
  for (const part of path.split('.')) {
    if (node === null || typeof node !== 'object'
        || !Object.prototype.hasOwnProperty.call(node, part)) return undefined;
    node = node[part];
  }
  return node;
}

export function foldNoteFor(chart, payload) {
  const spec = chart && FOLD_SPEC[chart.type];
  if (!spec) return '';
  const block = resolve(payload, chart.metricKey);
  const rows = Array.isArray(block) ? block
    : (block && block.tracked && Array.isArray(block.data) ? block.data : []);
  if (rows.length <= TOP_N) return '';

  const fold = topNWithRemainder(rows, spec.value, TOP_N);
  return foldCaption({
    count: fold.hiddenCount,
    value: fold.hiddenValue,
    opps: fold.hidden.reduce((a, r) => a + spec.opps(r), 0),
    noun: spec.noun,
    formatValue: spec.fmt,
  });
}

/* ── Rasterisation (browser only) ────────────────────────────────────────── */

/*
 * ⛔⛔ THE RASTERISER IS NOT WRITTEN HERE. src/pages/reports/_chartCapture.js
 * has done SVG → canvas → PNG since Card P156, for the Overview's PDF and
 * Word exports, and I wrote a second one in this file before checking —
 * precisely the mistake this repository keeps paying for (two stage
 * vocabularies, three markdown renderers, two receivables ageings). Card P225
 * caught it while looking for the helper it told me to reuse. The duplicate
 * is gone; there is one rasteriser.
 *
 * ⚠ captureSvgPng() takes a CONTAINER and returns bytes only, so the aspect
 * ratio is measured here from the same element. That is a smaller adapter
 * than a second implementation, and it keeps the capture logic — the white
 * ground, the data: URL, the never-reject contract — in one place.
 */

/**
 * Collect the rendered charts, keyed by the section index they belong to.
 * ⚠ Reads the DOM the reader is looking at — `root` is the report element.
 */
export async function collectCharts(root, capture = captureSvgPng) {
  const out = new Map();
  if (!root || typeof root.querySelectorAll !== 'function') return out;
  const sections = root.querySelectorAll('[data-testid="report-section"]');
  for (let i = 0; i < sections.length; i++) {
    const svg = sections[i].querySelector('svg');
    if (!svg) continue;
    // eslint-disable-next-line no-await-in-loop
    const bytes = await capture(sections[i]);
    if (!bytes) continue;
    const rect = svg.getBoundingClientRect ? svg.getBoundingClientRect() : null;
    const width = Math.max(1, Math.round((rect && rect.width) || svg.clientWidth || 600));
    const height = Math.max(1, Math.round((rect && rect.height) || svg.clientHeight || 220));
    out.set(i + 1, { bytes, width, height });
  }
  return out;
}

/* ── The document ────────────────────────────────────────────────────────── */

const GREY = rgb(0.42, 0.45, 0.5);
const INK = rgb(0, 0, 0);

/**
 * Build the PDF bytes from a model.
 *
 * @param {Array}  model     from buildReportModel()
 * @param {Map}    charts    section index -> { bytes, width, height }
 * @param {object} deps      { PDFDocument } — injected so a test can assert
 *                           the call without pdf-lib touching a canvas.
 */
export async function buildPdf(model, charts = new Map(), deps = {}) {
  const Doc = deps.PDFDocument || PDFDocument;
  const pdf = await Doc.create();
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);

  let page = pdf.addPage([PAGE.width, PAGE.height]);
  let y = PAGE.height - PAGE.margin;
  const left = PAGE.margin;
  const width = PAGE.width - PAGE.margin * 2;

  const newPage = () => { page = pdf.addPage([PAGE.width, PAGE.height]); y = PAGE.height - PAGE.margin; };
  const need = (h) => { if (y - h < PAGE.margin) newPage(); };

  /** Greedy wrap at `size`, returning the lines. */
  const wrap = (text, size, f, maxW) => {
    const words = String(text || '').split(/\s+/).filter(Boolean);
    const lines = [];
    let line = '';
    for (const w of words) {
      const next = line ? `${line} ${w}` : w;
      if (f.widthOfTextAtSize(next, size) > maxW && line) { lines.push(line); line = w; }
      else line = next;
    }
    if (line) lines.push(line);
    return lines;
  };

  const draw = (text, { size = 10, f = font, color = INK, indent = 0, gap = 3 } = {}) => {
    for (const line of wrap(text, size, f, width - indent)) {
      need(size + gap);
      page.drawText(line, { x: left + indent, y: y - size, size, font: f, color });
      y -= size + gap;
    }
  };

  for (const b of model) {
    switch (b.kind) {
      case 'header':
        draw(b.title, { size: 16, f: bold });
        draw(pdfSafe([b.pipeline, b.range, b.generated ? `generated ${b.generated}` : '']
          .filter(Boolean).join('  ·  ')), { size: 9, color: GREY, gap: 8 });
        y -= 6;
        break;
      case 'headline':
        draw(b.text, { size: 13, f: bold, gap: 5 });
        y -= 2;
        break;
      case 'summary':
        draw(b.text, { size: 10, color: GREY, gap: 4 });
        y -= 8;
        break;
      case 'kpis': {
        /* Three across, wrapping to a second row — the screen's strip. */
        const colW = width / 3;
        const rows = Math.ceil(b.tiles.length / 3);
        /* 52, not 46: a caption may take two lines (see the forEach below). */
        need(rows * 52 + 8);
        const top = y;
        b.tiles.forEach((t, i) => {
          const col = i % 3;
          const row = Math.floor(i / 3);
          const x = left + col * colW;
          const ty = top - row * 52;
          page.drawText(t.label, { x, y: ty - 9, size: 8, font, color: GREY });
          page.drawText(t.value, { x, y: ty - 24, size: 14, font: bold, color: INK });
          const sub = t.delta || t.caption;
          if (sub) {
            /*
             * ⛔⛔ EACH WRAPPED LINE NEEDS ITS OWN y. The first version drew
             * every line at the same height, so a two-line caption printed
             * ON TOP OF ITSELF — visible in the page-1 PNG as an unreadable
             * smear under "Unpaid invoices". jsdom cannot see this: there is
             * no layout, so the model was correct and the document was
             * wrong. A screenshot is the only control that catches it.
             */
            wrap(sub, 7, font, colW - 8).slice(0, 2).forEach((line, k) => {
              page.drawText(line, { x, y: ty - 34 - k * 9, size: 7, font, color: GREY });
            });
          }
        });
        y = top - rows * 52 - 10;
        break;
      }
      case 'note':
        draw(b.text, { size: 8, color: GREY, gap: 4 });
        y -= 6;
        break;
      case 'section': {
        /*
         * ⛔⛔ A SECTION AND ITS CHART START ON ONE PAGE. The card asks for no
         * section split; the honest version of that is "do not START a
         * section that cannot fit its heading, its bullets and its chart",
         * which is what this measures. A section longer than a whole page
         * still has to break somewhere, and breaking it is better than
         * dropping it.
         */
        const png = charts.get(b.index);
        const chartH = png ? Math.min(200, (png.height / png.width) * width) : 0;
        const estimate = 18 + b.bullets.length * 24 + chartH + 16;
        if (y - Math.min(estimate, PAGE.height - PAGE.margin * 2) < PAGE.margin) newPage();

        draw(`${b.index}. ${b.title}${b.status ? `   [${b.status.toUpperCase()}]` : ''}`,
          { size: 11, f: bold, gap: 5 });
        for (const bl of b.bullets) draw(pdfSafe(`-  ${bl}`), { size: 10, indent: 8, gap: 4 });

        if (png) {
          need(chartH + 10);
          try {
            const img = await pdf.embedPng(png.bytes);
            page.drawImage(img, { x: left, y: y - chartH, width, height: chartH });
            y -= chartH + 10;
          } catch (_) {
            /* A chart that will not embed costs the chart, not the document. */
          }
        }
        /* ⛔ Card P231 — the fold caption, as selectable text under the chart. */
        if (b.chartNote) draw(pdfSafe(b.chartNote), { size: 8, color: GREY, indent: 8, gap: 4 });
        y -= 6;
        break;
      }
      case 'risks':
        need(20); draw('Risks', { size: 11, f: bold, gap: 5 });
        for (const r of b.items) {
          draw(pdfSafe(`-  ${r.severity ? `${r.severity.toUpperCase()}: ` : ''}${r.text}`),
            { size: 10, indent: 8, gap: 4 });
        }
        y -= 6;
        break;
      case 'actions':
        need(20); draw('Recommended actions', { size: 11, f: bold, gap: 5 });
        for (const a of b.items) {
          draw(pdfSafe(`☐  ${a.text}${a.meta ? ` — ${a.meta}` : ''}`), { size: 10, indent: 8, gap: 4 });
        }
        break;
      default:
        break;
    }
  }

  /* ⚠ A footer on every page, written after the fact so "page X of Y" can
     know Y. The screen's print footer says the same thing. */
  const pages = pdf.getPages();
  const header = model.find((b) => b.kind === 'header') || {};
  pages.forEach((p, i) => {
    const text = pdfSafe(`ZignaGenix · AI pipeline report · ${header.pipeline || ''}`
      + `${header.range ? ` · ${header.range}` : ''} · page ${i + 1} of ${pages.length}`);
    p.drawText(text, { x: PAGE.margin, y: 28, size: 7, font, color: GREY });
  });

  return pdf.save();
}
